export type MessageStatus =
  | 'queued'
  | 'submitted'
  | 'sent'
  | 'delivered'
  | 'failed'
  | 'rejected'
  | 'expired'
  | 'unknown';

export type CampaignStatus =
  | 'draft'
  | 'scheduled'
  | 'queued'
  | 'processing'
  | 'paused'
  | 'completed'
  | 'cancelled'
  | 'failed';

export type OrganizationStatus = 'active' | 'suspended' | 'pending';

export type UserStatus = 'active' | 'suspended' | 'pending_verification';

export type KycStatus =
  | 'not_started'
  | 'pending'
  | 'needs_changes'
  | 'approved'
  | 'rejected';

export type SenderIdStatus =
  | 'pending'
  | 'under_review'
  | 'approved'
  | 'rejected'
  | 'expired';

export type InvoiceStatus =
  | 'draft'
  | 'issued'
  | 'partially_paid'
  | 'paid'
  | 'overdue'
  | 'void';

export type ProviderHealthStatus =
  | 'healthy'
  | 'degraded'
  | 'unavailable'
  | 'disabled';

export type Channel = 'sms' | 'ussd' | 'voice' | 'whatsapp' | 'email';

export type SmsEncoding = 'gsm7' | 'ucs2';

export interface Money {
  amount: number;
  currency: string;
}

export interface PaginationMeta {
  next_cursor?: string | null;
  has_more: boolean;
}

export interface ApiError {
  error: {
    code: string;
    message: string;
    request_id?: string;
    fields?: Record<string, string[]>;
  };
}

export const CUSTOMER_ROLES = [
  'owner',
  'admin',
  'developer',
  'campaign_manager',
  'finance',
  'analyst',
  'viewer',
] as const;

export type CustomerRole = (typeof CUSTOMER_ROLES)[number];

export const STAFF_ROLES = [
  'super_admin',
  'operations',
  'finance_admin',
  'compliance',
  'support',
  'engineering',
] as const;

export type StaffRole = (typeof STAFF_ROLES)[number];

export const PERMISSIONS = {
  'organization.read': 'View organization settings',
  'organization.update': 'Update organization settings',
  'organization.members.read': 'View team members',
  'organization.members.invite': 'Invite team members',
  'organization.members.update': 'Update team members',
  'organization.members.remove': 'Remove team members',
  'kyc.read': 'View KYC status',
  'kyc.submit': 'Submit KYC',
  'contacts.read': 'View contacts',
  'contacts.create': 'Create contacts',
  'contacts.update': 'Update contacts',
  'contacts.delete': 'Delete contacts',
  'contacts.import': 'Import contacts',
  'contacts.export': 'Export contacts',
  'sms.read': 'View SMS messages',
  'sms.send': 'Send SMS',
  'sms.send_bulk': 'Send bulk SMS',
  'sms.export': 'Export SMS messages',
  'campaigns.read': 'View campaigns',
  'campaigns.create': 'Create campaigns',
  'campaigns.update': 'Edit draft campaigns',
  'campaigns.send': 'Send campaigns',
  'campaigns.pause': 'Pause and resume campaigns',
  'campaigns.delete': 'Delete campaigns',
  'sender_ids.read': 'View sender IDs',
  'sender_ids.request': 'Request sender IDs',
  'developers.read': 'View developer settings',
  'api_keys.create': 'Create API keys',
  'api_keys.revoke': 'Revoke API keys',
  'webhooks.manage': 'Manage webhooks',
  'logs.read': 'View API logs',
  'analytics.read': 'View analytics',
  'analytics.export': 'Export analytics',
  'wallet.read': 'View wallet',
  'wallet.topup': 'Top up wallet',
  'transactions.read': 'View transactions',
  'invoices.read': 'View invoices',
  'billing.read': 'View billing',
  'billing.export': 'Export billing data',
  'reseller.customers.read': 'View reseller customers',
  'reseller.customers.create': 'Create reseller customer accounts',
  'reseller.credit.allocate': 'Allocate credit to customer accounts',
  'reseller.revenue.read': 'View reseller revenue',
  'reseller.branding.manage': 'Manage white-label branding',
  'support.read': 'View support tickets',
  'support.create': 'Create support tickets',
  'channels.read': 'View channel resources',
  'channels.manage': 'Manage USSD, Voice, WhatsApp',
  'ivr.read': 'View IVR Studio flows and executions',
  'ivr.manage': 'Create and edit IVR flows',
  'ivr.publish': 'Publish, pause and roll back IVR flows',
  'ai.use': 'Use MPAKANI AI assistants',
} as const;

export type PermissionKey = keyof typeof PERMISSIONS;

export const ROLE_PERMISSIONS: Record<CustomerRole, PermissionKey[]> = {
  owner: Object.keys(PERMISSIONS) as PermissionKey[],
  admin: Object.keys(PERMISSIONS) as PermissionKey[],
  developer: [
    'organization.read',
    'developers.read',
    'api_keys.create',
    'api_keys.revoke',
    'webhooks.manage',
    'logs.read',
    'sms.read',
    'sms.send',
    'sms.export',
    'analytics.read',
    'analytics.export',
    'wallet.read',
    'channels.read',
    'ivr.read',
    'ivr.manage',
  ],
  campaign_manager: [
    'organization.read',
    'contacts.read',
    'contacts.create',
    'contacts.update',
    'contacts.delete',
    'contacts.import',
    'contacts.export',
    'sms.read',
    'sms.send',
    'sms.send_bulk',
    'sms.export',
    'campaigns.read',
    'campaigns.create',
    'campaigns.update',
    'campaigns.send',
    'campaigns.pause',
    'campaigns.delete',
    'sender_ids.read',
    'sender_ids.request',
    'analytics.read',
    'analytics.export',
    'ai.use',
    'channels.read',
    'ivr.read',
    'ivr.manage',
    'ivr.publish',
  ],
  finance: [
    'organization.read',
    'billing.read',
    'billing.export',
    'wallet.read',
    'wallet.topup',
    'transactions.read',
    'invoices.read',
    'analytics.read',
  ],
  analyst: [
    'organization.read',
    'analytics.read',
    'analytics.export',
    'sms.read',
    'sms.export',
    'campaigns.read',
    'contacts.read',
    'ivr.read',
  ],
  viewer: [
    'organization.read',
    'sms.read',
    'campaigns.read',
    'analytics.read',
    'channels.read',
    'ivr.read',
  ],
};

export const DEFAULT_COUNTRY = 'TZ';
export const DEFAULT_CURRENCY = 'TZS';
export const DEFAULT_TIMEZONE = 'Africa/Dar_es_Salaam';
